import { useState } from 'react';
import { Button, Label, PasswordInput, Switch } from '@tutero/ui';

import { updateAccountPassword } from '@/lib/account';

import { Field, SettingsSection } from './SettingsSection';
import { reasonFor, useAccountWrite } from './useAccountWrite';

/** The Worker's own floor (`services/auth/src/auth.ts` › `minPasswordLength`), which this form enforces first. */
const FLOOR = 6;

/**
 * The password (`settings-is-a-dialog` D6).
 *
 * The switch is why this is not a two-field form: the Worker's
 * `revokeSessionsOnPasswordReset` governs the forgot-password RESET, not this, so a change
 * signs nothing else out unless it asks. On by default — a staff member changing a password
 * on the admin is usually answering an exposure — and their own session survives either
 * way, because the route revokes the others.
 */
export function AccountPasswordSection() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [signOutOthers, setSignOutOthers] = useState(true);
  const [done, setDone] = useState<string | null>(null);
  const change = useAccountWrite(updateAccountPassword);

  const short = next !== '' && next.length < FLOOR;
  const repeated = next !== '' && next === current;
  const mismatched = confirm !== '' && confirm !== next;
  const ready =
    current !== '' &&
    next.length >= FLOOR &&
    confirm === next &&
    next !== current;

  return (
    <SettingsSection
      title="Your password"
      about="Changing it here needs the password you have now."
      error={change.isError ? reasonFor(change.error) : null}
      done={done}
      onSubmit={() => {
        if (!ready || change.isPending) return;
        setDone(null);
        const others = signOutOthers;
        change.mutate(
          { currentPassword: current, newPassword: next, signOutOthers },
          {
            onSuccess: () => {
              setDone(
                others
                  ? 'Your password was changed, and your other sessions were signed out.'
                  : 'Your password was changed. Your other sessions were left signed in.',
              );
              setCurrent('');
              setNext('');
              setConfirm('');
            },
          },
        );
      }}
      footer={
        <Button type="submit" disabled={!ready || change.isPending}>
          {change.isPending ? 'Changing…' : 'Change Password'}
        </Button>
      }
    >
      <Field id="settings-current-password" label="Current password">
        <PasswordInput
          id="settings-current-password"
          value={current}
          autoComplete="current-password"
          onChange={(event) => {
            setCurrent(event.target.value);
            setDone(null);
          }}
        />
      </Field>

      <Field
        id="settings-new-password"
        label="New password"
        about={`At least ${FLOOR} characters.`}
      >
        <PasswordInput
          id="settings-new-password"
          value={next}
          autoComplete="new-password"
          onChange={(event) => {
            setNext(event.target.value);
            setDone(null);
          }}
        />
      </Field>
      {short ? (
        <p role="alert" className="text-destructive text-regular-s">
          Use {FLOOR} or more characters.
        </p>
      ) : null}
      {repeated ? (
        <p role="alert" className="text-destructive text-regular-s">
          The new password is the one you already have.
        </p>
      ) : null}

      <Field id="settings-confirm-password" label="Confirm new password">
        <PasswordInput
          id="settings-confirm-password"
          value={confirm}
          autoComplete="new-password"
          onChange={(event) => {
            setConfirm(event.target.value);
            setDone(null);
          }}
        />
      </Field>
      {mismatched ? (
        <p role="alert" className="text-destructive text-regular-s">
          Please make sure your passwords match.
        </p>
      ) : null}

      <div className="flex items-center gap-3">
        <Switch
          id="settings-sign-out-others"
          checked={signOutOthers}
          onCheckedChange={setSignOutOthers}
        />
        <Label htmlFor="settings-sign-out-others">
          Sign out my other sessions
        </Label>
      </div>
      <p className="text-muted-foreground text-regular-s">
        Every other browser and phone signed in as you is signed out. This one
        is not.
      </p>
    </SettingsSection>
  );
}
